import React, { useRef, useEffect, useState, useCallback } from 'react';
import './SignaturePad.css';

// ขนาดมาตรฐานของไฟล์ลายเซ็นที่ส่งออก (อัตราส่วน 2:1 เหมือน config ในเอกสาร) — พื้นหลังโปร่งใส
const EXPORT_W = 600;
const EXPORT_H = 300;
const PAD = 26;

// ตำแหน่งปลายปากกาภายในไอคอนเคอร์เซอร์ (svg coords) — ใช้จัดให้ปลายชี้จุดที่ลงเส้นพอดี
const PEN_TIP_X = 28;
const PEN_TIP_Y = 68;
const MAX_UNDO = 25;

// ค่าคงที่ของปากกา (ถอดตัวเลือกในแถบเครื่องมือออกหมด — ใช้ค่าตายตัว)
const PEN_SIZE = 8;             // ขนาดเส้น = ใหญ่สุด
const PEN_COLOR = '#111111';    // สีดำ
const PEN_TILT = 18;            // ทิศเอียงปากกา (มือขวา — สลับจากเดิมที่เป็น -18)
const PEN_SCALE = 1.25;         // ขนาดไอคอนปากกา = ใหญ่สุด + ใหญ่ขึ้นเล็กน้อย

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function SignaturePad({ onSave, onCancel, saving }) {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const cursorRef = useRef(null);
    const drawingRef = useRef(false);
    const ptsRef = useRef([]);
    const lastRef = useRef(null);
    const emaRef = useRef(null);
    const lastWidthRef = useRef(PEN_SIZE);
    const undoStackRef = useRef([]); // ImageData ก่อนเริ่มแต่ละเส้น

    const [isEmpty, setIsEmpty] = useState(true);
    const [undoSteps, setUndoSteps] = useState(0);

    const applyBasePen = useCallback((ctx) => {
        if (!ctx) return;
        ctx.lineWidth = PEN_SIZE;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = PEN_COLOR;
        ctx.fillStyle = PEN_COLOR;
    }, []);

    const setupCanvas = useCallback((preserve) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let prev = null;
        if (preserve && canvas.width > 0) {
            prev = document.createElement('canvas');
            prev.width = canvas.width;
            prev.height = canvas.height;
            prev.getContext('2d').drawImage(canvas, 0, 0);
        }
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(rect.width * dpr));
        canvas.height = Math.max(1, Math.round(rect.height * dpr));
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        applyBasePen(ctx);
        ctxRef.current = ctx;
        if (prev) ctx.drawImage(prev, 0, 0, canvas.width / dpr, canvas.height / dpr);
    }, [applyBasePen]);

    useEffect(() => {
        setupCanvas(false);
        const onResize = () => { undoStackRef.current = []; setUndoSteps(0); setupCanvas(true); };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, [setupCanvas]);

    useEffect(() => { applyBasePen(ctxRef.current); }, [applyBasePen]);

    const getPos = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const moveCursor = (x, y) => {
        const c = cursorRef.current;
        if (!c) return;
        c.style.transform = `translate(${x}px, ${y}px)`;
        c.style.opacity = '1';
    };
    const hideCursor = () => { if (cursorRef.current) cursorRef.current.style.opacity = '0'; };

    const pushUndo = () => {
        const canvas = canvasRef.current;
        const snap = ctxRef.current.getImageData(0, 0, canvas.width, canvas.height);
        undoStackRef.current.push(snap);
        if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift();
        setUndoSteps(undoStackRef.current.length);
    };

    const undo = () => {
        const stack = undoStackRef.current;
        if (stack.length === 0) return;
        const snap = stack.pop();
        ctxRef.current.putImageData(snap, 0, 0);
        setUndoSteps(stack.length);
        if (stack.length === 0) setIsEmpty(true);
    };

    const start = (e) => {
        e.preventDefault();
        try { canvasRef.current.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        pushUndo(); // เก็บภาพก่อนเริ่มเส้นใหม่ (สำหรับ undo)
        const raw = getPos(e);
        moveCursor(raw.x, raw.y);
        drawingRef.current = true;
        const t = performance.now();
        const p = { x: raw.x, y: raw.y, t };
        ptsRef.current = [p];
        lastRef.current = p;
        emaRef.current = { x: raw.x, y: raw.y };
        lastWidthRef.current = PEN_SIZE;
        const ctx = ctxRef.current;
        ctx.strokeStyle = PEN_COLOR;
        ctx.fillStyle = PEN_COLOR;
        ctx.beginPath();
        ctx.arc(raw.x, raw.y, PEN_SIZE / 2, 0, Math.PI * 2);
        ctx.fill();
        if (isEmpty) setIsEmpty(false);
    };

    const move = (e) => {
        const raw = getPos(e);
        moveCursor(raw.x, raw.y);
        if (!drawingRef.current) return;
        e.preventDefault();
        const ctx = ctxRef.current;
        const t = performance.now();

        // เส้นเนียน (beautify) เปิดตลอด — กรองมือสั่นด้วย EMA
        const ema = emaRef.current;
        ema.x += (raw.x - ema.x) * 0.5;
        ema.y += (raw.y - ema.y) * 0.5;
        const p = { x: ema.x, y: ema.y, t };

        const pts = ptsRef.current;
        pts.push(p);
        if (pts.length >= 3) {
            const n = pts.length;
            const p0 = pts[n - 3], p1 = pts[n - 2], p2 = pts[n - 1];
            const m1 = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
            const m2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
            // เส้นหนา-บางตามความเร็ว ให้ลายเซ็นดูสวย
            const maxW = PEN_SIZE * 1.35;
            const minW = PEN_SIZE * 0.5;
            const factor = (maxW - minW) / 1.6;
            const dt = Math.max(p2.t - p1.t, 1);
            const velocity = dist(p1, p2) / dt;
            const target = clamp(maxW - velocity * factor, minW, maxW);
            const w = lastWidthRef.current * 0.4 + target * 0.6;
            lastWidthRef.current = w;
            ctx.lineWidth = w;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(m1.x, m1.y);
            ctx.quadraticCurveTo(p1.x, p1.y, m2.x, m2.y);
            ctx.stroke();
        }
        lastRef.current = p;
    };

    const end = (e) => {
        drawingRef.current = false;
        ptsRef.current = [];
        if (e && e.pointerType && e.pointerType !== 'mouse') hideCursor();
    };

    const clear = () => {
        const canvas = canvasRef.current;
        ctxRef.current.clearRect(0, 0, canvas.width, canvas.height);
        undoStackRef.current = [];
        setUndoSteps(0);
        setIsEmpty(true);
    };

    const exportBlob = () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        const w = canvas.width, h = canvas.height;
        const data = ctx.getImageData(0, 0, w, h).data;
        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                if (data[(y * w + x) * 4 + 3] > 12) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
        }
        if (maxX < 0) return Promise.resolve(null);
        const bw = maxX - minX + 1, bh = maxY - minY + 1;
        const out = document.createElement('canvas');
        out.width = EXPORT_W;
        out.height = EXPORT_H;
        const octx = out.getContext('2d');
        const scale = Math.min((EXPORT_W - PAD * 2) / bw, (EXPORT_H - PAD * 2) / bh);
        const dw = bw * scale, dh = bh * scale;
        const dx = (EXPORT_W - dw) / 2, dy = (EXPORT_H - dh) / 2;
        octx.imageSmoothingEnabled = true;
        octx.imageSmoothingQuality = 'high';
        octx.drawImage(canvas, minX, minY, bw, bh, dx, dy, dw, dh);
        return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/png'));
    };

    const handleSave = async () => {
        const blob = await exportBlob();
        if (blob) onSave(blob);
    };

    return (
        <div className="sigpad-overlay" onClick={saving ? undefined : onCancel}>
            <div className="sigpad-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sigpad-head">
                    <span>เซ็นลายเซ็นของคุณ</span>
                    <button className="sigpad-x" onClick={onCancel} disabled={saving} aria-label="ปิด">&times;</button>
                </div>

                <div className="sigpad-canvas-wrap">
                    <canvas ref={canvasRef} className="sigpad-canvas"
                        onPointerDown={start}
                        onPointerMove={move}
                        onPointerUp={end}
                        onPointerLeave={(e) => { end(e); hideCursor(); }}
                        onPointerCancel={(e) => { end(e); hideCursor(); }}
                        onPointerEnter={(e) => { const p = getPos(e); moveCursor(p.x, p.y); }}
                    />
                    <span className="sigpad-baseline" aria-hidden="true"></span>
                    {isEmpty && <span className="sigpad-hint">✍️ เซ็นด้วยนิ้วหรือปากกาตรงนี้</span>}

                    <div ref={cursorRef} className="sigpad-cursor" style={{ opacity: 0 }} aria-hidden="true">
                        <span className="sigpad-cursor-ring"></span>
                        <svg className="sigpad-cursor-pen" width="56" height="72" viewBox="0 0 56 72"
                            style={{ transformOrigin: `${PEN_TIP_X}px ${PEN_TIP_Y}px`, transform: `scale(${PEN_SCALE})` }}>
                            <defs>
                                <linearGradient id="sigPenBody" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0" stopColor="#4b5563" />
                                    <stop offset="0.32" stopColor="#0b0d10" />
                                    <stop offset="0.62" stopColor="#2a2f37" />
                                    <stop offset="1" stopColor="#000000" />
                                </linearGradient>
                                <linearGradient id="sigPenMetal" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0" stopColor="#f1f5f9" />
                                    <stop offset="0.5" stopColor="#aeb8c4" />
                                    <stop offset="1" stopColor="#6b7480" />
                                </linearGradient>
                            </defs>
                            <g transform={`rotate(${PEN_TILT} ${PEN_TIP_X} ${PEN_TIP_Y})`}>
                                {/* คลิปหนีบโลหะ */}
                                <rect x="33.5" y="9" width="3" height="19" rx="1.5" fill="url(#sigPenMetal)" />
                                {/* ลำตัวปากกา (ไล่เฉดให้ดูเป็นมันวาว) */}
                                <path d="M20 11 Q20 6 28 6 Q36 6 36 11 L36 45 L20 45 Z" fill="url(#sigPenBody)" />
                                {/* แสงสะท้อนให้ดูเงาสวย */}
                                <rect x="23" y="11" width="2.6" height="31" rx="1.3" fill="#ffffff" opacity="0.30" />
                                {/* แหวนเงินคั่นด้ามจับ */}
                                <rect x="19.5" y="44" width="17" height="5.5" rx="1.6" fill="url(#sigPenMetal)" />
                                {/* ด้ามจับเรียวลงหาหัว */}
                                <path d="M21 49.5 L35 49.5 L31 60 L25 60 Z" fill="#101317" />
                                {/* หัวปากกาโลหะ */}
                                <path d="M25 60 L31 60 L28 68 Z" fill="url(#sigPenMetal)" />
                                {/* ร่อง + รูระบายกลางหัวปากกา */}
                                <line x1="28" y1="61" x2="28" y2="67" stroke="#5b6573" strokeWidth="0.9" />
                                <circle cx="28" cy="61.3" r="1" fill="#5b6573" />
                                {/* จุดปลายสุด = จุดที่ลงเส้นพอดี */}
                                <circle cx="28" cy="68" r="1.4" fill="#0b0d10" />
                            </g>
                        </svg>
                    </div>
                </div>

                <div className="sigpad-actions">
                    <button className="steam-btn steam-btn-secondary" onClick={undo} disabled={saving || undoSteps === 0}>
                        ↩ เลิกทำ
                    </button>
                    <button className="steam-btn steam-btn-secondary" onClick={clear} disabled={saving || isEmpty}>ล้าง</button>
                    <div className="sigpad-spacer" />
                    <button className="steam-btn steam-btn-secondary" onClick={onCancel} disabled={saving}>ยกเลิก</button>
                    <button className="steam-btn steam-btn-primary" onClick={handleSave} disabled={saving || isEmpty}>
                        {saving ? 'กำลังบันทึก...' : 'บันทึกลายเซ็น'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SignaturePad;
